// client/src/components/SidebarLayout.js
// Main layout with branded sidebar navigation
// UPDATED Jul 27 2026: Added "Organization" link at top of nav for quick access to settings.
//                      Positioned before Projects section with hover states and active highlighting.
// UPDATED Aug 11 2026: Enhanced top bar with project name, campaign name, and company icon roundel.
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { UserButton, useUser } from '@clerk/clerk-react';
import CampaignScorecard from './CampaignScorecard';
// Load Work Sans font
if (typeof document !== 'undefined') {
  const fontLink = document.createElement('link');
  fontLink.href = 'https://fonts.googleapis.com/css2?family=Work+Sans:wght@300;400;500;600;700;800&display=swap';
  fontLink.rel = 'stylesheet';
  if (!document.querySelector('link[href*="Work+Sans"]')) {
    document.head.appendChild(fontLink);
  }
}
// Brand colors
const colors = {
  primary: '#39393a',
  secondary: '#048ba8',
  tertiary: '#e9c46a',
  accent: '#f4a261',
  grey: '#e76f51',
  green: '#01796f'
};
// Small colour cue per ENQ status, shown as a dot next to each project.
const STATUS_DOT = {
  not_started: '#9ca3af',
  discovery_pending: colors.accent,
  discovery_running: colors.accent,
  discovery_complete: colors.secondary,
  discovery_failed: colors.grey,
  entities_confirmed: colors.green
};
// Extract a bare hostname from whatever the project stores (full URL or bare domain).
const getProjectDomain = (project) => {
  const raw = project.primary_domain || project.domain || project.website_url || '';
  if (!raw) return null;
  try {
    return new URL(raw.startsWith('http') ? raw : `https://${raw}`).hostname;
  } catch {
    return raw.replace(/^https?:\/\//, '').split('/')[0] || null;
  }
};
const DEMO_MODE_KEY = 'geojetpack_demo_mode';
const SidebarLayout = ({ children, currentCampaign, campaigns, apiBaseUrl }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const { user } = useUser();
  const userId = user?.id;
  // Track which projects are expanded (Set of project ids).
  // Separate from navigation — chevron click only, not name click.
  const [expandedProjectIds, setExpandedProjectIds] = useState(new Set());
  const [hoveredButton, setHoveredButton] = useState(null);
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  // Demo mode — persisted to localStorage so it survives refreshes during a live demo.
  const [demoMode, setDemoMode] = useState(() => {
    try {
      return localStorage.getItem(DEMO_MODE_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const toggleDemoMode = () => {
    setDemoMode((prev) => {
      const next = !prev;
      try { localStorage.setItem(DEMO_MODE_KEY, String(next)); } catch {}
      if (next && activeProjectIdFromRoute) {
        setExpandedProjectIds((ids) => new Set([...ids, activeProjectIdFromRoute]));
      }
      if (next && campaignId && campaigns) {
        const c = campaigns.find((c) => String(c.id) === String(campaignId));
        if (!c?.project_id) {
          setExpandedProjectIds((ids) => new Set([...ids, 'unassigned']));
        }
      }
      return next;
    });
  };
  // Current campaign id from params or the passed-in campaign.
  const campaignId = params.campaignId || currentCampaign?.id;
  const isActive = (path) => location.pathname.includes(path);
  // Toggle a single project's expand state without navigating.
  const toggleProject = (projectId) => {
    setExpandedProjectIds((prev) => {
      const next = new Set(prev);
      if (next.has(projectId)) {
        next.delete(projectId);
      } else {
        next.add(projectId);
      }
      return next;
    });
  };
  // Derive the active project id from the current route or active campaign.
  const activeProjectIdFromRoute = (() => {
    const m = location.pathname.match(/\/project\/([a-f0-9-]+)/);
if (m) return m[1];
    if (campaignId && campaigns) {
      const c = campaigns.find((c) => String(c.id) === String(campaignId));
      if (c?.project_id) return c.project_id;
    }
    return null;
  })();
  // Get the active project and campaign for the top bar
  const activeProject = activeProjectIdFromRoute
    ? projects.find((p) => p.id === activeProjectIdFromRoute)
    : null;
  const activeCampaign = campaignId
    ? campaigns?.find((c) => String(c.id) === String(campaignId))
    : null;
  const activeProjectDomain = activeProject ? getProjectDomain(activeProject) : null;
  // Context-aware top bar title
  const isProjectRoute   = location.pathname.includes('/project/');
  const isScorecardRoute = isProjectRoute && location.pathname.includes('/scorecard');
  const rawTopBarTitle = isScorecardRoute ? 'Project Scorecard'
                       : isProjectRoute   ? 'Entity Bank'
                       : (currentCampaign?.name || 'Dashboard');
  const topBarTitle = rawTopBarTitle;
  const topBarSubtitle = isScorecardRoute
    ? 'Aggregate performance across all campaigns in this project'
    : isProjectRoute
      ? 'Your project entity bank — the foundation for all campaigns'
      : 'Optimize your content for AI search engines';
  // ── Fetch the org's projects ──
  useEffect(() => {
    let cancelled = false;
    const loadProjects = async () => {
      if (!apiBaseUrl || !userId) {
        setProjectsLoading(false);
        return;
      }
      try {
        setProjectsLoading(true);
        const res = await fetch(`${apiBaseUrl}/projects`, {
          headers: { 'x-user-id': userId }
        });
        const json = await res.json();
        if (!cancelled && res.ok && json.success) {
          setProjects(json.projects || []);
        } else if (!cancelled) {
          setProjects([]);
        }
      } catch (err) {
        if (!cancelled) setProjects([]);
      } finally {
        if (!cancelled) setProjectsLoading(false);
      }
    };
    loadProjects();
    return () => { cancelled = true; };
  }, [apiBaseUrl, userId]);
  // Auto-expand the project that owns the active campaign or active project route
  useEffect(() => {
    const projectRouteMatch = location.pathname.match(/\/project\/(\d+)/);
    if (projectRouteMatch) {
      setExpandedProjectIds((prev) => new Set([...prev, Number(projectRouteMatch[1])]));
    }
    if (campaignId && campaigns) {
      const activeCampaign = campaigns.find((c) => String(c.id) === String(campaignId));
      if (activeCampaign?.project_id) {
        setExpandedProjectIds((prev) => new Set([...prev, activeCampaign.project_id]));
      }
    }
  }, [location.pathname, campaignId, campaigns]);
  // ── Group campaigns under their project ──
  const campaignsByProject = {};
  const unassignedCampaigns = [];
  (campaigns || []).forEach((c) => {
    if (c.project_id) {
      if (!campaignsByProject[c.project_id]) campaignsByProject[c.project_id] = [];
      campaignsByProject[c.project_id].push(c);
    } else {
      unassignedCampaigns.push(c);
    }
  });
  // ── In demo mode, filter to only the active project ──
  const visibleProjects = demoMode
    ? projects.filter((p) => p.id === activeProjectIdFromRoute)
    : projects;
  const visibleUnassigned = demoMode
    ? unassignedCampaigns.filter((c) => String(c.id) === String(campaignId))
    : unassignedCampaigns;
  // ── Shared sub-item style (campaign workflow items) ──
  const subItemStyle = (pathFragment) => ({
    width: '100%',
    textAlign: 'left',
    fontSize: '13px',
    padding: '6px 12px',
    borderRadius: '6px',
    backgroundColor: isActive(`/${pathFragment}`) ? colors.grey : hoveredButton === pathFragment ? '#1f2937' : 'transparent',
    color: isActive(`/${pathFragment}`) ? 'white' : '#d1d5db',
    fontWeight: isActive(`/${pathFragment}`) ? '600' : '400',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.2s'
  });
  // ── Render a single campaign's expand tree ──
  const renderCampaignChildren = (c) => (
    <div
      key={`children-${c.id}`}
      style={{
        marginLeft: '14px',
        paddingLeft: '12px',
        borderLeft: '1px solid #4b5563',
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
        marginTop: '2px',
        marginBottom: '4px'
      }}
    >
      {[
        { label: 'Prompts',        path: 'prompts', route: `/campaign/${c.id}/prompts` },
        { label: 'Content',        path: 'urls',    route: `/campaign/${c.id}/urls` },
        { label: 'Offsite',        path: 'site',    route: `/campaign/${c.id}/site` },
        { label: 'Report builder', path: 'reports', route: `/campaign/${c.id}/reports` },
      ].map(({ label, path, route }) => (
        <button
          key={path}
          onClick={() => navigate(route)}
          onMouseEnter={() => setHoveredButton(path)}
          onMouseLeave={() => setHoveredButton(null)}
          style={subItemStyle(path)}
        >
          {label}
        </button>
      ))}
      <button
        onClick={() => navigate(`/campaign/${c.id}/testing`)}
        onMouseEnter={() => setHoveredButton(`testing-${c.id}`)}
        onMouseLeave={() => setHoveredButton(null)}
        style={{
          width: '100%',
          textAlign: 'left',
          fontSize: '13px',
          padding: '6px 12px',
          borderRadius: '6px',
          backgroundColor: hoveredButton === `testing-${c.id}` ? '#1f2937' : 'transparent',
          color: '#9ca3af',
          fontWeight: '400',
          border: 'none',
          cursor: 'pointer',
          transition: 'all 0.2s',
          display: 'flex',
          alignItems: 'center'
        }}
      >
        <span>Testing</span>
        <span style={{
          marginLeft: 'auto', fontSize: '10px', backgroundColor: '#374151',
          padding: '1px 6px', borderRadius: '9999px', fontWeight: '600', color: colors.secondary
        }}>BETA</span>
      </button>
    </div>
  );
  // ── Render a list of campaign rows (used both inside projects and for unassigned) ──
  const renderCampaignList = (campaignList) =>
    campaignList.map((c) => {
      const isActiveCampaign = String(c.id) === String(campaignId);
      const displayName = c.name;
      return (
        <div key={c.id}>
          <button
            onClick={() => navigate(`/campaign/${c.id}/summary`)}
            onMouseEnter={() => setHoveredButton(`campaign-${c.id}`)}
            onMouseLeave={() => setHoveredButton(null)}
            style={{
              width: '100%',
              textAlign: 'left',
              fontSize: '13px',
              padding: '6px 12px',
              borderRadius: '6px',
              backgroundColor: isActiveCampaign
                ? '#374151'
                : hoveredButton === `campaign-${c.id}` ? '#1f2937' : 'transparent',
              color: isActiveCampaign ? 'white' : '#d1d5db',
              border: 'none',
              cursor: 'pointer',
              transition: 'all 0.2s',
              fontWeight: isActiveCampaign ? '500' : '400',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {displayName}
          </button>
          {isActiveCampaign && renderCampaignChildren(c)}
        </div>
      );
    });
  return (
    <div style={{
      display: 'flex',
      height: '100vh',
      backgroundColor: '#f9fafb',
      fontFamily: "'Work Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
    }}>
      {/* Sidebar */}
      <div style={{
        width: '256px',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: colors.primary
      }}>
        {/* Logo */}
        <div style={{ padding: '24px', borderBottom: `1px solid ${colors.secondary}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
            <img
              src="/PNG_White_Jetpack_Logo_300dpi.png"
              alt="GEO Jetpack"
              style={{ height: '80px', width: 'auto' }}
            />
          </div>
        </div>
        {/* Navigation - Scrollable */}
        <nav style={{
          flex: 1,
          overflowY: 'auto',
          padding: '16px',
          display: 'flex',
          flexDirection: 'column',
          gap: '4px'
        }}>
          {/* ── ORGANIZATION — link to settings ── */}
          <button
            onClick={() => navigate('/settings')}
            onMouseEnter={() => setHoveredButton('organization')}
            onMouseLeave={() => setHoveredButton(null)}
            style={{
              width: '100%',
              textAlign: 'left',
              fontSize: '13px',
              padding: '8px 12px',
              borderRadius: '6px',
              backgroundColor: isActive('/settings') ? colors.secondary : hoveredButton === 'organization' ? '#1f2937' : 'transparent',
              color: isActive('/settings') ? 'white' : '#d1d5db',
              border: 'none',
              cursor: 'pointer',
              transition: 'all 0.2s',
              fontWeight: isActive('/settings') ? '600' : '500',
              fontFamily: "'Work Sans', sans-serif",
              marginBottom: '12px'
            }}
          >
            Organization
          </button>
          {/* ── PROJECTS — static section label ── */}
          <div style={{
            fontSize: '11px',
            fontWeight: '600',
            color: '#6b7280',
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            padding: '4px 12px 8px'
          }}>
            Projects
          </div>
          {projectsLoading && (
            <div style={{ fontSize: '13px', color: '#9ca3af', padding: '4px 12px' }}>Loading…</div>
          )}
          {!projectsLoading && visibleProjects.length === 0 && !demoMode && (
            <div style={{ fontSize: '13px', color: '#9ca3af', padding: '4px 12px' }}>No projects yet</div>
          )}
          {/* Demo mode — empty state when no active project context */}
          {!projectsLoading && demoMode && visibleProjects.length === 0 && visibleUnassigned.length === 0 && (
            <div style={{ fontSize: '13px', color: '#6b7280', padding: '4px 12px', fontStyle: 'italic' }}>
              Navigate to a project or campaign to show it here
            </div>
          )}
          {/* ── Project rows ── */}
          {!projectsLoading && visibleProjects.map((project) => {
            const isExpanded      = expandedProjectIds.has(project.id);
            const isActiveProject = location.pathname.includes(`/project/${project.id}`);
            const projectDomain   = getProjectDomain(project);
            const projectCampaigns = campaignsByProject[project.id] || [];
            const displayProjectName = project.name;
            return (
              <div key={project.id}>
                {/* Project row — name navigates, chevron toggles */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '7px 8px',
                    borderRadius: '6px',
                    backgroundColor: isActiveProject
                      ? '#1f2937'
                      : hoveredButton === `project-${project.id}` ? '#1f2937' : 'transparent',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={() => setHoveredButton(`project-${project.id}`)}
                  onMouseLeave={() => setHoveredButton(null)}
                >
                  {/* ENQ status dot */}
                  <span style={{
                    width: '6px',
                    height: '6px',
                    borderRadius: '9999px',
                    flexShrink: 0,
                    backgroundColor: STATUS_DOT[project.enq_status] || '#9ca3af'
                  }} />
                  {/* Favicon */}
                  {projectDomain && !demoMode && (
                    <span style={{
                      width: 20, height: 20, borderRadius: '50%', backgroundColor: 'white',
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                    }}>
                      <img
                        src={`https://www.google.com/s2/favicons?domain=${projectDomain}&sz=32`}
                        alt=""
                        width={13}
                        height={13}
                        style={{ borderRadius: 2 }}
                        onError={(e) => { e.target.parentElement.style.display = 'none'; }}
                      />
                    </span>
                  )}
                  {/* Project name — navigates to scorecard */}
                  <button
                    onClick={() => navigate(`/project/${project.id}/scorecard`)}
                    style={{
                      flex: 1,
                      textAlign: 'left',
                      fontSize: '14px',
                      fontWeight: isActiveProject ? '600' : '500',
                      color: '#e5e7eb',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {displayProjectName}
                  </button>
                  {/* Chevron — toggles expand only */}
                  <button
                    onClick={() => toggleProject(project.id)}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '0 2px',
                      color: '#9ca3af',
                      fontSize: '10px',
                      flexShrink: 0,
                      lineHeight: 1
                    }}
                    aria-label={isExpanded ? 'Collapse project' : 'Expand project'}
                  >
                    {isExpanded ? '▾' : '▸'}
                  </button>
                </div>
                {/* Project children */}
                {isExpanded && (
                  <div style={{
                    marginLeft: '14px',
                    paddingLeft: '12px',
                    borderLeft: '1px solid #4b5563',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '2px',
                    marginTop: '2px',
                    marginBottom: '6px'
                  }}>
                    {/* Scorecard */}
                    <button
                      onClick={() => navigate(`/project/${project.id}/scorecard`)}
                      onMouseEnter={() => setHoveredButton(`scorecard-${project.id}`)}
                      onMouseLeave={() => setHoveredButton(null)}
                      style={{
                        width: '100%',
                        textAlign: 'left',
                        fontSize: '13px',
                        padding: '6px 12px',
                        borderRadius: '6px',
                        backgroundColor: isActive(`/project/${project.id}/scorecard`)
                          ? colors.secondary
                          : hoveredButton === `scorecard-${project.id}` ? '#1f2937' : 'transparent',
                        color: isActive(`/project/${project.id}/scorecard`) ? 'white' : '#d1d5db',
                        border: 'none',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        fontWeight: '500'
                      }}
                    >
                      Project scorecard
                    </button>
                    {/* Entity Bank */}
                    <button
                      onClick={() => navigate(`/project/${project.id}/discovery`)}
                      onMouseEnter={() => setHoveredButton(`discovery-${project.id}`)}
                      onMouseLeave={() => setHoveredButton(null)}
                      style={{
                        width: '100%',
                        textAlign: 'left',
                        fontSize: '13px',
                        padding: '6px 12px',
                        borderRadius: '6px',
                        backgroundColor: isActive(`/project/${project.id}/discovery`)
                          ? colors.secondary
                          : hoveredButton === `discovery-${project.id}` ? '#1f2937' : 'transparent',
                        color: isActive(`/project/${project.id}/discovery`) ? 'white' : '#d1d5db',
                        border: 'none',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        fontWeight: '500'
                      }}
                    >
                      Entity bank
                    </button>
                    {/* Campaigns sub-section */}
                    {projectCampaigns.length > 0 && (
                      <>
                        <div style={{
                          fontSize: '10px',
                          fontWeight: '600',
                          color: '#6b7280',
                          letterSpacing: '0.06em',
                          textTransform: 'uppercase',
                          padding: '8px 12px 2px'
                        }}>
                          Campaigns
                        </div>
                        {renderCampaignList(projectCampaigns)}
                      </>
                    )}
                    {/* Empty state if no campaigns yet */}
                    {projectCampaigns.length === 0 && (
                      <div style={{ fontSize: '12px', color: '#6b7280', padding: '4px 12px' }}>
                        No campaigns yet
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          {/* ── Unassigned campaigns (legacy, no project_id) ── */}
          {visibleUnassigned.length > 0 && (
            <div style={{ marginTop: '8px' }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '7px 8px',
                  borderRadius: '6px',
                  backgroundColor: hoveredButton === 'unassigned' ? '#1f2937' : 'transparent',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={() => setHoveredButton('unassigned')}
                onMouseLeave={() => setHoveredButton(null)}
              >
                <span style={{ fontSize: '14px', fontWeight: '500', color: '#9ca3af' }}>
                  Unassigned
                </span>
                <button
                  onClick={() => toggleProject('unassigned')}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    padding: '0 2px', color: '#6b7280', fontSize: '10px', lineHeight: 1
                  }}
                  aria-label={expandedProjectIds.has('unassigned') ? 'Collapse unassigned' : 'Expand unassigned'}
                >
                  {expandedProjectIds.has('unassigned') ? '▾' : '▸'}
                </button>
              </div>
              {expandedProjectIds.has('unassigned') && (
                <div style={{
                  marginLeft: '14px',
                  paddingLeft: '12px',
                  borderLeft: '1px solid #4b5563',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '2px',
                  marginTop: '2px',
                  marginBottom: '6px'
                }}>
                  {renderCampaignList(visibleUnassigned)}
                </div>
              )}
            </div>
          )}
        </nav>
        {/* Bottom Actions */}
        <div style={{ padding: '16px', borderTop: `1px solid ${colors.secondary}` }}>
          {/* Demo Mode toggle */}
          <button
            onClick={toggleDemoMode}
            onMouseEnter={() => setHoveredButton('demo-mode')}
            onMouseLeave={() => setHoveredButton(null)}
            style={{
              width: '100%',
              marginBottom: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '8px 12px',
              borderRadius: '8px',
              border: `1px solid ${demoMode ? colors.accent : '#4b5563'}`,
              backgroundColor: demoMode ? 'rgba(244,162,97,0.12)' : 'transparent',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
          >
            <span style={{
              fontSize: '13px',
              fontWeight: '600',
              color: demoMode ? colors.accent : '#9ca3af',
              letterSpacing: '0.02em'
            }}>
              {demoMode ? '● Demo mode on' : '○ Demo mode'}
            </span>
            {/* Pill toggle */}
            <span style={{
              display: 'inline-flex',
              width: '32px',
              height: '18px',
              borderRadius: '9999px',
              backgroundColor: demoMode ? colors.accent : '#4b5563',
              position: 'relative',
              transition: 'background 0.2s',
              flexShrink: 0
            }}>
              <span style={{
                position: 'absolute',
                top: '3px',
                left: demoMode ? '17px' : '3px',
                width: '12px',
                height: '12px',
                borderRadius: '9999px',
                backgroundColor: 'white',
                transition: 'left 0.2s',
                boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
              }} />
            </span>
          </button>
          {/* New Project — organisation-level action, always available */}
          <button
            onClick={() => navigate('/project/new')}
            onMouseEnter={() => setHoveredButton('new-project')}
            onMouseLeave={() => setHoveredButton(null)}
            style={{
              width: '100%',
              marginBottom: '8px',
              padding: '10px 16px',
              borderRadius: '8px',
              fontWeight: '600',
              fontSize: '14px',
              color: colors.secondary,
              backgroundColor: hoveredButton === 'new-project'
                ? 'rgba(4,139,168,0.10)'
                : 'transparent',
              border: `1px solid ${colors.secondary}`,
              cursor: 'pointer',
              transition: 'all 0.2s',
              transform: hoveredButton === 'new-project' ? 'scale(1.02)' : 'scale(1)'
            }}
          >
            + New project
          </button>

          <button
            onClick={() => navigate(`/project/${activeProjectIdFromRoute}/campaign-builder`)}
            onMouseEnter={() => setHoveredButton('new-campaign')}
            onMouseLeave={() => setHoveredButton(null)}
            style={{
              width: '100%', padding: '10px 16px', borderRadius: '8px',
              fontWeight: '600', fontSize: '14px', color: 'white',
              backgroundColor: colors.grey, border: 'none', cursor: 'pointer',
              boxShadow: hoveredButton === 'new-campaign' ? '0 10px 15px rgba(0,0,0,0.2)' : '0 4px 6px rgba(0,0,0,0.1)',
              transition: 'all 0.2s',
              transform: hoveredButton === 'new-campaign' ? 'scale(1.02)' : 'scale(1)'
            }}
          >
            + New campaign
          </button>
          <button
            onClick={() => navigate('/settings')}
            onMouseEnter={() => setHoveredButton('settings')}
            onMouseLeave={() => setHoveredButton(null)}
            style={{
              width: '100%', marginTop: '8px', display: 'flex', alignItems: 'center', gap: '12px',
              padding: '7px 8px', borderRadius: '6px', color: '#d1d5db',
              backgroundColor: hoveredButton === 'settings' ? '#1f2937' : 'transparent',
              border: 'none', cursor: 'pointer', transition: 'all 0.2s',
              fontSize: '14px', fontWeight: '500'
            }}
          >
            <span>Settings</span>
          </button>
        </div>
      </div>
      {/* Main Content Area */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Top Bar */}
        <div style={{ backgroundColor: 'white', borderBottom: '1px solid #e5e7eb', padding: '16px 24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            {/* LEFT SIDE — Context (Project + Campaign + Icon) */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
              {activeProject && activeProjectDomain && (
                <>
                  {/* Company Icon Roundel */}
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    backgroundColor: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    border: `2px solid ${colors.secondary}`,
                    overflow: 'hidden'
                  }}>
                    <img
                      src={`https://www.google.com/s2/favicons?domain=${activeProjectDomain}&sz=32`}
                      alt=""
                      width={32}
                      height={32}
                      style={{ borderRadius: '50%' }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                  {/* Project and Campaign Text */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
                    {/* Project Name — Large, Bold */}
                    <div style={{
                      fontSize: '18px',
                      fontWeight: '700',
                      color: colors.primary,
                      lineHeight: '1.2',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {activeProject.name}
                    </div>
                    {/* Campaign Name — Smaller, CAPS */}
                    {activeCampaign && (
                      <div style={{
                        fontSize: '12px',
                        fontWeight: '600',
                        color: colors.secondary,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        lineHeight: '1.2',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {activeCampaign.name}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
            {/* RIGHT SIDE — Demo badge, Help, UserButton */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              {/* Demo mode badge in top bar */}
              {demoMode && (
                <span style={{
                  fontSize: '11px',
                  fontWeight: '700',
                  color: colors.accent,
                  border: `1px solid ${colors.accent}`,
                  borderRadius: '6px',
                  padding: '3px 8px',
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase'
                }}>
                  Demo
                </span>
              )}
              <a
                href="mailto:hello@geojetpack.ai"
                style={{ padding: '8px 16px', fontSize: '14px', fontWeight: '500', color: '#374151', textDecoration: 'none', transition: 'all 0.2s' }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#111827'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#374151'}
              >
                Help
              </a>
              <UserButton afterSignOutUrl="/sign-in" />
            </div>
          </div>
        </div>
        {/* STICKY CAMPAIGN SCORECARD — only on campaign routes */}
        {currentCampaign && apiBaseUrl && (
          <CampaignScorecard campaign={currentCampaign} apiBaseUrl={apiBaseUrl} />
        )}
        {/* Page Content */}
        <div style={{ flex: 1, overflow: 'auto', backgroundColor: '#f9fafb' }}>
          {children}
        </div>
      </div>
    </div>
  );
};
export default SidebarLayout;
